import { useState, useEffect } from 'react';
import PubNub from 'pubnub';
import { Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import CustomConfirmationModal from '../reusable/CustomConfirmationModal';
import Panel from '../reusable/Panel';
import Loader from '../reusable/Loader';
import CustomDivider from '../reusable/CustomDivider';
import SurveyQuestionsList from './SurveyQuestionsList';
import SurveyLinks from './SurveyLinks';
import {
  SurveyListItem,
  SurveyQuestionDetailResponse,
  SurveyTextUpdatedMessage,
  getSurveyQuestions,
  deleteQuestion,
} from '../../services/surveyService';
import { USER_ROLES } from '../../constants';
import ArrowDown from '../assets/arrow-down.svg';

interface ExpandedSurveyData {
  [surveyId: string]: {
    questions: SurveyQuestionDetailResponse[];
    loading: boolean;
  };
}

interface SurveysListProps {
  surveys: SurveyListItem[];
  onSurveyQuestionDeleted: (surveyId: string) => void;
}

const SurveysList = ({ surveys, onSurveyQuestionDeleted }: SurveysListProps) => {
  const {
    userDetails: { data: userDetails },
    pubNubToken,
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const [expandedSurveys, setExpandedSurveys] = useState<ExpandedSurveyData>({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<{
    surveyId: string;
    questionId: string;
    questionText: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [latestSurveyUpdate, setLatestSurveyUpdate] = useState<SurveyTextUpdatedMessage | null>(null);

  const canEdit = userDetails?.role === USER_ROLES.SUPER_ADMIN || userDetails?.role === USER_ROLES.XQ_ADMIN;

  useEffect(() => {
    if (!userDetails || !pubNubToken) return;

    const pubnub = new PubNub({
      subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE_KEY as string,
      userId: userDetails.id,
    });
    pubnub.setToken(pubNubToken);

    const channel = pubnub.channel(`eoa.${userDetails.eoaId}.surveyupdated`);
    const subscription = channel.subscription();

    pubnub.addListener({
      status: (status) => {
        if (status.error) console.error(`PubNub error: ${status.category}`);
      },
    });

    subscription.onMessage = (event: any) => {
      setLatestSurveyUpdate(event.message as SurveyTextUpdatedMessage);
    };

    subscription.subscribe();

    return () => {
      pubnub.removeAllListeners();
      pubnub.unsubscribeAll();
    };
  }, [userDetails, pubNubToken]);

  const handleSurveyExpanded = async (surveyId: string, isExpanding: boolean) => {
    if (!userDetails) return;

    if (isExpanding && !expandedSurveys[surveyId]) {
      setExpandedSurveys((prev) => ({
        ...prev,
        [surveyId]: { questions: [], loading: true },
      }));

      try {
        const questions = await getSurveyQuestions(surveyId, userDetails.access_token);
        setExpandedSurveys((prev) => ({
          ...prev,
          [surveyId]: { ...prev[surveyId], questions, loading: false },
        }));
      } catch (error: any) {
        console.error('Error fetching survey questions:', error);
        const errorMessage = error?.response?.data?.detail || 'Failed to fetch survey questions. Please try again.';
        handleOpenErrorModal(errorMessage, error as Error);
        setExpandedSurveys((prev) => ({
          ...prev,
          [surveyId]: { ...prev[surveyId], loading: false },
        }));
      }
    }
  };

  const handleDeleteClick = (surveyId: string, questionId: string, questionText: string) => {
    setQuestionToDelete({ surveyId, questionId, questionText });
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userDetails || !questionToDelete) return;

    setIsDeleting(true);

    try {
      await deleteQuestion(questionToDelete.surveyId, questionToDelete.questionId, userDetails.access_token);

      setExpandedSurveys((prev) => ({
        ...prev,
        [questionToDelete.surveyId]: {
          ...prev[questionToDelete.surveyId],
          questions: prev[questionToDelete.surveyId].questions.filter((q) => q.id !== questionToDelete.questionId),
        },
      }));

      onSurveyQuestionDeleted(questionToDelete.surveyId);

      setDeleteModalOpen(false);
      setQuestionToDelete(null);
    } catch (error: any) {
      console.error('Error deleting question:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to delete question. Please try again.';
      handleOpenErrorModal(errorMessage, error as Error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (surveys.length === 0) {
    return (
      <Panel className="survey-management-panel">
        <p className="survey-management-empty-state">No surveys found. Create your first survey above.</p>
      </Panel>
    );
  }

  return (
    <Panel className="survey-management-panel">
      <h2 className="survey-management-section-title">Existing Surveys</h2>
      <div className="survey-management-surveys-list">
        {surveys.map((survey) => (
          <div key={survey.id} className="survey-management-survey-item">
            <Accordion
              onChange={(_, isExpanded) => handleSurveyExpanded(survey.id, isExpanded)}
              sx={{ backgroundColor: 'var(--neutral-white)' }}
            >
              <AccordionSummary expandIcon={<img alt="expand accordion icon" src={ArrowDown} />}>
                <div className="survey-management-survey-header">
                  <div className="survey-management-survey-info">
                    <div className="survey-management-survey-title-row">
                      <Tooltip title={survey.survey_name} arrow placement="top">
                        <h3 className="survey-management-survey-name">{survey.survey_name}</h3>
                      </Tooltip>
                      <span className="survey-management-audience-name">({survey.audience_name})</span>
                    </div>
                    <div className="survey-management-survey-meta">
                      <span>Version: {survey.survey_version}</span>
                      <span>•</span>
                      <span>{survey.question_count} questions</span>
                      <span>•</span>
                      <span>Created: {new Date(survey.created_on).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                {survey.survey_url && (
                  <>
                    <SurveyLinks
                      surveyId={survey.id}
                      surveyUrl={survey.survey_url}
                      languageUrls={survey.language_urls || {}}
                      externalId={survey.external_id}
                    />
                    <CustomDivider orientation="horizontal" size="small" />
                  </>
                )}
                {expandedSurveys[survey.id]?.loading ? (
                  <Loader />
                ) : (
                  <SurveyQuestionsList
                    questions={expandedSurveys[survey.id]?.questions || []}
                    onDeleteQuestion={(questionId, questionText) =>
                      handleDeleteClick(survey.id, questionId, questionText)
                    }
                    eoaSurveyId={survey.id}
                    canEdit={canEdit}
                    surveyUpdatedMessage={latestSurveyUpdate}
                  />
                )}
              </AccordionDetails>
            </Accordion>
            <CustomDivider orientation="horizontal" size="small" />
          </div>
        ))}
      </div>

      <CustomConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setQuestionToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Question"
        message={
          <>
            <p>Are you sure you want to delete this question?</p>
            <p style={{ marginTop: '12px', fontWeight: 'bold' }}>"{questionToDelete?.questionText}"</p>
            <p style={{ marginTop: '12px', fontSize: '14px', color: 'var(--text-gray)' }}>
              This will remove the question from Typeform and all responses to this question will still exist but
              reference a deleted question.
            </p>
          </>
        }
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        maxWidth="sm"
      />
    </Panel>
  );
};

export default SurveysList;
